import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  CookiePreferences as GeneratedCookiePreferences,
  CookiePreferencesInput,
  useSetCookieConsentMutation,
} from '@/generated/graphql';
import {
  CookieCategory,
  defaultPreferences,
  loadCookieConsent,
  saveCookieConsent,
  clearCookieConsent,
  isCategoryConsented,
} from '../lib/cookies';

// Use the generated type instead of local interface
type CookiePreferences = GeneratedCookiePreferences;

interface CookieConsentState {
  hasConsented: boolean;
  preferences: CookiePreferences;
}

interface CookieConsentContextValue {
  hasConsented: boolean;
  preferences: CookiePreferences;
  showBanner: boolean;
  showPreferences: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (preferences: CookiePreferences) => void;
  openPreferences: () => void;
  closePreferences: () => void;
  isCategoryConsented: (category: CookieCategory) => boolean;
  resetConsent: () => void;
}

const CookieConsentContext = createContext<
  CookieConsentContextValue | undefined
>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider',
    );
  }
  return context;
};

interface CookieConsentProviderProps {
  children: React.ReactNode;
  onConsentChange?: (preferences: CookiePreferences) => void;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({
  children,
  onConsentChange,
}) => {
  const { user } = useAuth();
  const [setCookieConsent] = useSetCookieConsentMutation();
  // Initialize state based on user's backend consent or local storage
  const [state, setState] = useState<CookieConsentState>(() => {
    // If user is logged in and has consent from backend, use that
    if (user?.cookieConsent) {
      return {
        hasConsented: true,
        preferences: user.cookieConsent,
      };
    }

    // Otherwise, check local storage
    const savedConsent = loadCookieConsent();
    return (
      savedConsent || {
        hasConsented: false,
        preferences: defaultPreferences,
      }
    );
  });

  const [showBanner, setShowBanner] = useState(() => {
    // Don't show banner if user has consent from backend
    if (user?.cookieConsent) {
      return false;
    }

    // Otherwise check local storage
    const savedConsent = loadCookieConsent();
    return !savedConsent;
  });

  const [showPreferences, setShowPreferences] = useState(false);

  // Update state when user changes (login/logout)
  useEffect(() => {
    if (user?.cookieConsent) {
      setState({
        hasConsented: true,
        preferences: user.cookieConsent,
      });
      setShowBanner(false);
    } else {
      // User logged out or has no consent - check local storage
      const savedConsent = loadCookieConsent();
      setState(
        savedConsent || {
          hasConsented: false,
          preferences: defaultPreferences,
        },
      );
      setShowBanner(!savedConsent);
    }
  }, [user]);

  // Notify parent component when consent changes
  useEffect(() => {
    if (state.hasConsented && onConsentChange) {
      onConsentChange(state.preferences);
    }
  }, [state.hasConsented, state.preferences, onConsentChange]);

  const saveConsentToBackend = useCallback(
    async (preferences: CookiePreferences) => {
      if (user) {
        try {
          const input: CookiePreferencesInput = {
            functional: preferences.functional,
            analytics: preferences.analytics,
            marketing: preferences.marketing,
            consentDate: preferences.consentDate || new Date().toISOString(),
          };

          await setCookieConsent({
            variables: { preferences: input },
          });
        } catch (error) {
          console.error('Failed to save cookie consent to backend:', error);
          // Fall back to local storage if backend save fails
          saveCookieConsent(preferences);
        }
      } else {
        // No user logged in, save to local storage
        saveCookieConsent(preferences);
      }
    },
    [user, setCookieConsent],
  );

  const acceptAll = useCallback(async () => {
    const newPreferences: CookiePreferences = {
      functional: true,
      analytics: true,
      marketing: true,
      consentDate: new Date().toISOString(),
    };

    await saveConsentToBackend(newPreferences);
    setState({
      hasConsented: true,
      preferences: newPreferences,
    });
    setShowBanner(false);
    setShowPreferences(false);
  }, [saveConsentToBackend]);

  const rejectAll = useCallback(async () => {
    const newPreferences: CookiePreferences = {
      functional: true, // Always required
      analytics: false,
      marketing: false,
      consentDate: new Date().toISOString(),
    };

    await saveConsentToBackend(newPreferences);
    setState({
      hasConsented: true,
      preferences: newPreferences,
    });
    setShowBanner(false);
    setShowPreferences(false);
  }, [saveConsentToBackend]);

  const savePreferences = useCallback(
    async (preferences: CookiePreferences) => {
      // Ensure functional cookies are always enabled
      const finalPreferences: CookiePreferences = {
        ...preferences,
        functional: true,
        consentDate: preferences.consentDate || new Date().toISOString(),
      };

      await saveConsentToBackend(finalPreferences);
      setState({
        hasConsented: true,
        preferences: finalPreferences,
      });
      setShowBanner(false);
      setShowPreferences(false);
    },
    [saveConsentToBackend],
  );

  const openPreferences = useCallback(() => {
    setShowPreferences(true);
  }, []);

  const closePreferences = useCallback(() => {
    setShowPreferences(false);
    // If user hasn't consented yet, show the banner again
    if (!state.hasConsented) {
      setShowBanner(true);
    }
  }, [state.hasConsented]);

  const resetConsent = useCallback(async () => {
    clearCookieConsent();

    // If user is logged in, clear consent from backend too
    if (user) {
      try {
        const emptyPreferences: CookiePreferencesInput = {
          functional: true,
          analytics: false,
          marketing: false,
          consentDate: null,
        };

        await setCookieConsent({
          variables: { preferences: emptyPreferences },
        });
      } catch (error) {
        console.error('Failed to clear cookie consent from backend:', error);
      }
    }

    setState({
      hasConsented: false,
      preferences: defaultPreferences,
    });
    setShowBanner(true);
    setShowPreferences(false);
  }, [user, setCookieConsent]);

  const value: CookieConsentContextValue = {
    hasConsented: state.hasConsented,
    preferences: state.preferences,
    showBanner,
    showPreferences,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
    isCategoryConsented,
    resetConsent,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};
